import {
	type BulkAclEntity,
	type CreateMarketViewRequest,
	type CustomMarketViewSettings,
	type DefaultMarketViewSettings,
	type MarketViewAssetClassAliases,
	type MarketViewType,
	AclEntityMinInfoEntityTypeEnum,
	MarketViewControllerApiFactory,
	OutlookControllerV3ApiFactory,
	PortfolioStudioPreferencesApiFactory,
	ScenarioAnalysisControllerApiFactory,
} from "@eu.mdotm.api/sphere";
import type { RichAccessControl } from "@eu.mdotm.api/smart-workspaces";
import { getApiGen } from "$root/api/factory";
import { repositoryHelperFactory } from "$root/api/repository";
import { type EventPayloadMap, eventBus } from "$root/event-bus";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { seconds } from "$root/utils";
import type { Abortable } from "@mdotm/utils/promise";
import debounce from "debounce";

const marketViewRepositoryBaseKeys = {
	list: "marketViewList",
};

const { newQuery, newAction, invalidateLists, invalidateEntity, invalidate, invalidateEntities } =
	repositoryHelperFactory("marketView", {
		listKeys: Object.values(marketViewRepositoryBaseKeys),
	});

const configMarketViewApi = () => getApiGen(MarketViewControllerApiFactory);
const configPortfolioStudioPreference = () => getApiGen(PortfolioStudioPreferencesApiFactory);
const configScenarioAnalysis = () => getApiGen(ScenarioAnalysisControllerApiFactory);

export const marketViewRepository = {
	invalidateLists,
	invalidateEntity,

	list: newQuery(marketViewRepositoryBaseKeys.list, () => axiosExtract(configMarketViewApi().getMarketViewList())),

	summary: newQuery("summary", (uuid: string) => axiosExtract(configMarketViewApi().getMarketView(uuid))),

	assetClassAliases: newQuery("assetClassAliases", () =>
		axiosExtract(configPortfolioStudioPreference().getMarketViewAssetClassAliases()),
	),

	columnOrdering: newQuery(marketViewRepositoryBaseKeys.list, () =>
		axiosExtract(configPortfolioStudioPreference().getUserMarketViewColumnMetricsOrderingPreferences()),
	),

	scenarioList: newQuery(
		"scenarioList",
		(opts: {
			includeHistorical?: boolean;
			investmentUuid?: string;
			useEnhancement?: boolean;
			marketViewType?: MarketViewType;
			custom?: boolean;
		}) =>
			axiosExtract(
				configMarketViewApi().getMarketViewScenarioList(
					opts.includeHistorical,
					opts.investmentUuid,
					opts.useEnhancement,
					opts.marketViewType,
					opts.custom,
				),
			),
	),

	scenario: newQuery(
		"scenario",
		(opts: { scenarioId: string; investmentUuid?: string; useEnhancement?: boolean; custom?: boolean }) =>
			axiosExtract(
				configMarketViewApi().getMarketViewScenario(
					opts.scenarioId,
					opts.investmentUuid,
					opts.useEnhancement,
					opts.custom,
				),
			),
	),

	assetClassMetricsStandard: newQuery(
		"assetClassSummary",
		(opts: {
			assetClass:
				| "EQUITY"
				| "FIXED_INCOME"
				| "COMMODITIES"
				| "FIXEDINCOME"
				| "equity"
				| "fixed_income"
				| "fixedIncome"
				| "commodities"
				| "CO"
				| "FI"
				| "EQ";
			geography:
				| "EM"
				| "JP"
				| "US"
				| "EU"
				| "GLOBAL"
				| "em"
				| "jp"
				| "us"
				| "eu"
				| "global"
				| "UK"
				| "uk"
				| "EU_SMALL_MID_CAP"
				| "eu_small_mid_cap"
				| "PACIFIC_EX_JP"
				| "pacific_ex_jp"
				| "US_SMALL_CAP"
				| "us_small_cap"
				| "US_MID_CAP"
				| "us_mid_cap";
			sector:
				| "ALL"
				| "ALL_SECTORS"
				| "GLOBAL"
				| "PRECIOUS_METALS"
				| "ENERGY"
				| "FINANCIALS"
				| "INDUSTRIALS"
				| "CONSUMER_STAPLES"
				| "COMMUNICATION_SERVICES"
				| "INFORMATION_TECHNOLOGY"
				| "CONSUMER_DISCRETIONARY"
				| "HEALTHCARE"
				| "HEALTH_CARE"
				| "MATERIALS"
				| "UTILITIES"
				| "REAL_ESTATE"
				| "AGRICULTURE"
				| "INDUSTRIAL_METALS"
				| "LONG_GOV"
				| "MEDIUM_GOV"
				| "SHORT_GOV"
				| "CORP"
				| "GOV"
				| "HY"
				| "all"
				| "global"
				| "precious_metals"
				| "energy"
				| "financials"
				| "industrials"
				| "consumer_staples"
				| "communication_services"
				| "information_technology"
				| "consumer_discretionary"
				| "healthcare"
				| "health_care"
				| "materials"
				| "utilities"
				| "real_estate"
				| "agriculture"
				| "industrial_metals"
				| "long_gov"
				| "medium_gov"
				| "short_gov"
				| "corp"
				| "gov"
				| "commodities"
				| "hy";
		}) =>
			axiosExtract(
				getApiGen(OutlookControllerV3ApiFactory).retrieveAcMetricsStandard(
					opts.assetClass,
					opts.geography,
					opts.sector,
				),
			),
	),

	create: newAction(async (opts: CreateMarketViewRequest) => {
		const response = await axiosExtract(configMarketViewApi().createMarketView(opts));
		invalidateLists();
		return response;
	}),
	delete: newAction(async (uuid: string) => {
		await configMarketViewApi().deleteMarketView(uuid);
		invalidateLists();
	}),
	duplicate: newAction(async (uuid: string, name: string) => {
		const response = await axiosExtract(configMarketViewApi().duplicateMarketView(uuid, name));
		invalidateLists();
		return response;
	}),
	rename: newAction(async (uuid: string, name: string) => {
		await axiosExtract(configMarketViewApi().renameMarketView(uuid, name));
		invalidateEntity(uuid);
	}),
	isMarketViewNameAvailable: newAction((name: string, opts?: Abortable) => {
		return axiosExtract(configMarketViewApi().isMarketViewNameAvailable(name, opts));
	}),

	analysisScenarioList: newQuery("analysisScenarioList", (uuid: string, enhanced: boolean) =>
		axiosExtract(configScenarioAnalysis().getScenarioList(uuid, enhanced)),
	),

	analysisScenario: newQuery("analysisScenario", (scenarioId: string, uuid?: string, enhanced?: boolean) =>
		axiosExtract(configScenarioAnalysis().loadScenario(scenarioId, uuid, enhanced)),
	),

	marketViewSettingsCustomConfiguration: newQuery("marketViewSettingsCustomConfiguration", () =>
		axiosExtract(configPortfolioStudioPreference().getCustomMarketViewSettings()),
	),

	marketViewSettingsStandardConfiguration: newQuery(
		"marketViewSettingsStandardConfiguration",
		(marketViewType: MarketViewType) =>
			axiosExtract(configPortfolioStudioPreference().getDefaultMarketViewSettings(marketViewType)),
	),

	marketViewSettings: newQuery("settings", () => axiosExtract(configMarketViewApi().getMarketViewUserSettings())),

	marketViewSettingsSelectableAssetClasses: newQuery(
		"marketViewSettingsSelectableAssetClasses",
		(marketViewType: MarketViewType, custom?: boolean) =>
			axiosExtract(configPortfolioStudioPreference().getSelectableAssetClasses(marketViewType, custom)),
	),

	marketViewSettingsSelectableScenario: newQuery(
		"marketViewSettingsSelectableScenario",
		(marketViewType: MarketViewType) =>
			axiosExtract(configPortfolioStudioPreference().getSelectableScenarioIds(marketViewType)),
	),

	editStandardMarketViewSettings: newAction(async (newSettings: DefaultMarketViewSettings) => {
		await axiosExtract(configPortfolioStudioPreference().setDefaultMarketViewSettings(newSettings));
		invalidate(marketViewRepository.marketViewSettings.partialQueryKey);
		invalidate(marketViewRepository.marketViewSettingsStandardConfiguration.partialQueryKey);
	}),

	editCustomMarketViewSettings: newAction(async (newSettings: CustomMarketViewSettings) => {
		await axiosExtract(configPortfolioStudioPreference().setCustomMarketViewSettings(newSettings));
		invalidate(marketViewRepository.marketViewSettings.partialQueryKey);
		invalidate(marketViewRepository.marketViewSettingsCustomConfiguration.partialQueryKey);
	}),

	editMarketViewSettingsAliases: newAction(async (newMarketViewAssetClassAliases: MarketViewAssetClassAliases) => {
		await axiosExtract(
			configPortfolioStudioPreference().setMarketViewAssetClassAliases(newMarketViewAssetClassAliases),
		);
		invalidate(marketViewRepository.marketViewSettings.partialQueryKey);
		invalidate(marketViewRepository.assetClassAliases.partialQueryKey);
	}),

	getACL: newQuery("acl", (uuid: string) => axiosExtract(configMarketViewApi().getAclEditStarter3(uuid))),

	updateACL: newAction(async (uuid: string, acl: Array<RichAccessControl>) => {
		await configMarketViewApi().setACL3(uuid, acl);
		invalidateEntity(uuid);
	}),

	bulkShare: newAction(async (bulkAclEntities: BulkAclEntity[]) => {
		await axiosExtract(configMarketViewApi().setBulkACL3(bulkAclEntities));
		invalidateEntities(bulkAclEntities.map((entity) => entity.entityUuid!));
	}),

	bulkSharePreview: newQuery("bulkSharePreview", async (acl: BulkAclEntity[]) =>
		axiosExtract(configMarketViewApi().getBulkACLCheck3(acl)),
	),
};

eventBus.addListener(
	"shared-entity",
	debounce((e: EventPayloadMap["shared-entity"]) => {
		if (e.sharedEntityType === AclEntityMinInfoEntityTypeEnum.MarketView) {
			invalidateEntity(e.sharedEntityUuid ?? "");
		}
	}, seconds(2)),
);

eventBus.addListener(
	"shared-entities",
	debounce((e: EventPayloadMap["shared-entities"]) => {
		const referenceEntities = e.investments?.filter((x) => x.sharedEntityType === "MARKET_VIEW");
		if (referenceEntities?.length && referenceEntities?.length > 0) {
			invalidateEntities(referenceEntities.flatMap((x) => (x.sharedEntityUuid ? [x.sharedEntityUuid] : [])));
		}
	}, seconds(2)),
);
